import RiotControl from 'riotcontrol'
import logger from '../logger'
import {
  filter,
  assign,
} from 'lodash-es'
import extendedObservable from './base'
import api from '../api'
import AjaxUtils from '../utils/ajax-utils'
import PIEUtils from '../utils/pie-utils'
import AnalyticsStrings from '../metrics/strings'
import appProps from '#util/app-props'


// //////////
// Tenders //
// //////////

/* global ApplePaySession */

class Tenders {
  constructor() {
    const orderResourceKey = 'tenders'
    extendedObservable(this, 'tendersModel', { tenders: [] })

    let applePaySession = null
    let applePayPaymentRequest = null

    function tenderDto() {
      return {
        number: '',
        maskedNumber: '',
        sequenceId: 0,
        amount: 0,
        balance: 0,
        expiration: '',
        isRei: 'P',
        pin: '',
        payerId: '',
        email: '',
        type: '',
        status: 'NEW',
        isILA: false,
        validation: {
          hash: '',
          timestamp: 0,
          lifespan: 0,
        },
      }
    }

    const successHandler = (json, status, xhr) => {
      logger('Success: Communicated with OP Tenders Service - ', 'info')()
      logger(json, 'info')()

      this.model.tenders = json
      RiotControl.trigger('updateOrderResource', orderResourceKey, this.model.tenders)

      // update the order checksum
      const checksum = xhr.getResponseHeader('checksum')
      RiotControl.trigger('updateOrderChecksum', checksum)

      return this.model
    }

    const deleteHandler = (id, xhr) => {
      const tenders = this.orderModel.safeGet('tenders')

      // If a tender has never been added `tenders` will be null,
      // if a tender has been added then removed it will be an empty array.
      if (tenders && tenders.length) {
        this.orderModel.tenders = filter(tenders, tender => tender.number !== id)
      }

      // update the order checksum
      const checksum = xhr.getResponseHeader('checksum')
      RiotControl.trigger('updateOrderChecksum', checksum)

      return this.orderModel.tenders
    }

    const errorHandler = (xhr, status, errorThrown) => {
      logger(`Error: Failed to communicate with OP Service - ${errorThrown}`, 'error')()
      logger(`Status: ${status}`, 'error')()
      logger(xhr, 'error')()

      // If checksum is invalid
      if (AjaxUtils.checkForBadChecksum(xhr)) {
        AjaxUtils.handleChecksumMismatch()
      }

      if (AjaxUtils.checkForNoOrder(xhr)) {
        AjaxUtils.handleNoOrder()
      }

      // update the order checksum
      const checksum = xhr.getResponseHeader('checksum')
      RiotControl.trigger('updateOrderChecksum', checksum)

      // hiding the global overlay screen after async stuff happens
      RiotControl.trigger('hideGlobalOverlay')
      return xhr
    }

    const addTender = (data) => {
      // check to see if there is already an order on the model,
      // if not hide the overlay and reject
      if (this.model === undefined) {
        RiotControl.trigger('hideGlobalOverlay')
        throw new Error('No Tenders Model')
      }

      const requestData = assign(tenderDto(), data)

      // Stringify the request
      return api.tender.post(JSON.stringify(requestData))
        .then(successHandler)
        .fail(errorHandler)
    }


    // ////////////
    // Apple Pay //
    // ////////////

    // Event Handlers //

    /**
     * Merchant Validation
     * We call our (REI) merchant session endpoint, passing the URL to use
     */
    const validateMerchantHandler = (event) => {
      logger('Apple Pay :: Begin Validate merchant', 'info')()
      api.applePay.createSession(event.validationURL)
        .then((response) => {
          logger('Apple Pay :: Validate merchant success. BE session created ->', 'info')()
          logger(response, 'info')()
          applePaySession.completeMerchantValidation(response)
        })
        .fail((err) => {
          logger('Apple Pay :: Validate merchant failure. Failed to create BE session: ->', 'error')()
          logger(err, 'info')()
          RiotControl.trigger('applePayMerchantValidationFailure', err)
          applePaySession.abort()
        })
    }

    /**
     * Payment Authorization
     * Here you receive the encrypted payment data. You would then send it
     * on to your payment provider for processing, and return an appropriate
     * status in session.completePayment()
     */
    const paymentAuthorizedHandler = (event) => {
      // Send payment for processing...
      const payment = event.payment

      RiotControl.trigger('analytics.sendClick', {
        linkName: AnalyticsStrings.link.submitApplePayOrder,
      })

      logger('Apple Pay :: payment authorized ->', 'info')()
      logger(event, 'info')()

      // Call checkout BE to submit Apple Pay order
      RiotControl.trigger('submitApplePayOrder', payment.token.paymentData, (response) => {
        logger('Apple Pay :: submit order success. order submitted to REI BE ->', 'info')()
        logger(response, 'info')()
        applePaySession.completePayment(ApplePaySession.STATUS_SUCCESS)

        RiotControl.trigger('updateOrderSubmitProgress')
        // Trigger the order review page to move to order confirmation
        RiotControl.trigger('applePayOrderSubmitted')
      }, (err) => {
        logger('Apple Pay :: submit order failure. ->', 'error')()
        logger(err, 'error')()
        RiotControl.trigger('applePayOrderFailure', err)
        applePaySession.completePayment(ApplePaySession.STATUS_FAILURE)
      })
    }

    /**
     * Handler for when the user changes their payment card in the Apple Pay session
     * @param  {Event} event Apple Pay event
     * @return {none}       no return
     */
    const paymentMethodSelectedHandler = (event) => {
      // Payment method changed
      const update = {
        newTotal: applePayPaymentRequest.total,
      }

      logger('Apple Pay :: user changed payment method: ->', 'info')()
      logger(event, 'info')()
      applePaySession.completePaymentMethodSelection(update)
    }

    /**
     * Handler for when the Apple Pay session is canceled
     * @return {none} no return
     */
    const cancelHandler = (event) => {
      // Apple Pay canceled
      RiotControl.trigger('analytics.sendClick', {
        linkName: AnalyticsStrings.link.cancelApplePay,
      })
      logger('Apple Pay :: canceled ->', 'info')()
      logger(event, 'info')()
      applePaySession.abort()
      applePaySession = null
    }


    // Main methods //

    /**
     * Initializes the Apple Pay session and sets up event handlers
     * @return {none} no return
     */
    const initApplePaySession = () =>
      Promise.resolve()
        .then(buildApplePaymentRequest)
        .then(initAppleSession)
        .then(wireAppleEvents)
        .then(beginAppleSession)
        .catch((err) => {
          logger('something went wrong ;( ->', 'info')()
          logger(err, 'info')()
        })

    /**
     * Builds the payment request which is sent to apple when
     * the session is created
     * @return {Object} Apple payment request object
     */
    const buildApplePaymentRequest = () => {
      applePayPaymentRequest = {
        merchantCapabilities: ['supports3DS'],
        supportedNetworks: [
          'amex',
          'chinaUnionPay',
          'discover',
          'masterCard',
          'visa',
        ],
        countryCode: 'US',
        total: {
          label: 'REI Co-op',
          type: 'final',
          amount: this.orderModel.safeGet('balance', 0),
        },
        currencyCode: 'USD',
      }

      return applePayPaymentRequest
    }

    /**
     * Initializes the Apple Pay session through the browser API
     * @param  {Object} applePayPaymentRequest Apple payment request object
     * @return {Object}                        Apple Pay session object
     */
    const initAppleSession = (paymentRequest) => {
      applePaySession = new ApplePaySession(3, paymentRequest)
      logger(applePaySession, 'info')()
      return applePaySession
    }

    /**
     * Wires the event handlers for the Apple Pay session object
     * @param  {Object} session Apple Pay session object
     * @return {Object}         Apple Pay session object
     */
    const wireAppleEvents = (session) => {
      // eslint-disable-next-line no-param-reassign
      session.onpaymentmethodselected = paymentMethodSelectedHandler

      // eslint-disable-next-line no-param-reassign
      session.oncancel = cancelHandler

      // eslint-disable-next-line no-param-reassign
      session.onvalidatemerchant = validateMerchantHandler

      // eslint-disable-next-line no-param-reassign
      session.onpaymentauthorized = paymentAuthorizedHandler

      // After manipulation, return the session
      return session
    }

    /**
     * Begins the apple pay session
     * @param  {Object} session Apple Pay session object
     * @return {none}         no return
     */
    const beginAppleSession = (session) => {
      session.begin()
    }

    this.storeCreditCard = data => new Promise((resolve) => {
      // When feature flag for encryption is enabled, run encryption.
      const { number, pin, type } = data

      // patch for 12 0's
      // reset special CC
      RiotControl.trigger('updateAppModel', 'testCCEnabled', false)
      if (number === '000000000000') {
        RiotControl.trigger('updateAppModel', 'testCCEnabled', true)
      }

      // If encryption is endabled, encrypt.
      // do not attempt to encrypt the special 12 0 card.
      // do not attempt to encrypt saved CCs since the number is masked.
      const encryptionEnabled = appProps.get('chase.end-to-end-encryption.enabled', false)
      if (encryptionEnabled && !this.appModel.testCCEnabled && type !== 'CREDITCARD_REPOSITORY') {
        const encrypted = PIEUtils.doEncryption(number, pin, false)
        this.model.encryptedStoredCreditCard = encrypted
      }

      this.model.storedCreditCard = data
      resolve()
    })

    const deleteStoredCreditCard = () => new Promise((resolve) => {
      delete this.model.storedCreditCard
      resolve()
    })

    this.on('registerTendersObserver', observer => this.registerObserver(observer))
    this.on('unregisterTendersObserver', observer => this.unregisterObserver(observer))

    // //////////////
    // Collection //
    // //////////////
    this.on('getTenders', (cb) => {
      logger('getTenders', 'debug')()
      api.tenders.get()
        .then(successHandler)
        .then(this.notifyObservers)
        .then(cb)
        .fail(errorHandler)
        .fail((err) => {
          logger(err, 'debug')()
        })
    })

    // ////////////
    // Singular //
    // ////////////
    this.on('addTender', (data, cb, errorCb) => {
      logger('addTender', 'debug')()
      addTender(data)
        .then(this.notifyObservers)
        .then(cb)
        .catch((err) => {
          logger(err, 'debug')()
          errorCb(err)
        })
    })

    this.on('getTender', (id, cb) => {
      logger('getTender', 'debug')()
      api.tender.get(id)
        .then(successHandler)
        .then(this.notifyObservers)
        .then(cb)
        .fail(errorHandler)
        .fail((err) => {
          logger(err, 'debug')()
        })
    })

    this.on('deleteTender', (id, cb, errorCb) => {
      logger('deleteTender', 'debug')()
      api.tender.delete(id)
        .then((json, status, xhr) => deleteHandler(id, xhr))
        .then(this.notifyObservers)
        .then(cb)
        .fail(errorHandler)
        .fail((err) => {
          logger(err, 'debug')()
          errorCb(err)
        })
    })


    // /////////////////////
    // Stored Credit Card //
    // /////////////////////
    this.on('storeCreditCard', (data, cb) => {
      logger('storeCreditCard', 'debug')()
      this.storeCreditCard(data)
        .then(this.notifyObservers)
        .then(cb)
        .catch((err) => {
          logger(err, 'debug')()
        })
    })

    this.on('deleteStoredCreditCard', (cb) => {
      logger('deleteStoredCreditCard', 'debug')()
      deleteStoredCreditCard()
        .then(this.notifyObservers)
        .then(cb)
        .catch((err) => {
          logger(err, 'debug')()
        })
    })


    // ////////////
    // Apple Pay //
    // ////////////
    this.on('initApplePaySession', (cb) => {
      logger('Apple Pay :: Initializing Apple Pay Session', 'debug')()
      initApplePaySession()
        .then(this.notifyObservers)
        .then(cb)
        .catch((err) => {
          logger(err, 'debug')()
        })
    })
  }
}

export const TendersInstance = new Tenders()